import { useMemo, useState } from 'react'

export type SortDirection = 'asc' | 'desc'

export interface SortConfig {
  key: string
  direction: SortDirection
}

/**
 * Generic client-side sort for an already-filtered array. Apply this to the
 * fully filtered list BEFORE any pagination slice, never to an already-paged
 * subset -- sorting only the visible page would silently stop reordering
 * rows that live on other pages.
 */
export function useSortableData<T extends Record<string, any>>(data: T[], initialSort?: SortConfig) {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(initialSort ?? null)

  const sortedData = useMemo(() => {
    if (!sortConfig) return data
    const { key, direction } = sortConfig
    return [...data].sort((a, b) => {
      const va = a[key]
      const vb = b[key]
      if (va == null && vb == null) return 0
      if (va == null) return 1
      if (vb == null) return -1
      const cmp =
        typeof va === 'number' && typeof vb === 'number'
          ? va - vb
          : String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' })
      return direction === 'asc' ? cmp : -cmp
    })
  }, [data, sortConfig])

  const requestSort = (key: string) => {
    setSortConfig((prev) => {
      if (!prev || prev.key !== key) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return null
    })
  }

  return { sortedData, sortConfig, requestSort }
}
